import { Button, Form, Input, Select } from 'antd';
import { useTranslation } from 'translations/hooks';

import { MainCard } from 'ui/components/main-card';
import {
  SegmentStatus,
  UpdatableSegmentStatuses,
} from 'business/receipt/constants';

import type { FormInstance } from 'antd/lib';
import {
  QuarantineDefectOrientationEnum,
  type SegmentFormValues,
} from 'business/receipt/pages/choose-segment-status/types';
import { useMemo } from 'react';
import type { DefectTypeDto } from 'generated/apiSchemas';

import segmentDrawDown from 'ui/segment/down.png';
import segmentDrawUp from 'ui/segment/up.png';
import { ImagePointer } from 'business/receipt/components/image-pointer';
import { PictureUploadInput } from 'business/receipt/components/picture-upload-input';

interface StatusFormProps {
  form: FormInstance<SegmentFormValues>;
  save: (row: SegmentFormValues) => void;
  segmentTypes: string[] | null;
  quarantineDefectTypes: DefectTypeDto[];
  initialValues: { segmentType?: string; status?: string };
  isFetching: boolean;
  isQuarantineEnabled: boolean;
  isOnHoldOptionEnabled: boolean;
}

export const StatusForm = ({
  form,
  save,
  segmentTypes,
  quarantineDefectTypes,
  initialValues,
  isFetching,
  isQuarantineEnabled,
  isOnHoldOptionEnabled,
}: StatusFormProps) => {
  const { t } = useTranslation();

  const selectedDefectLocalization = Form.useWatch('localization', form);
  const selectedStatus = Form.useWatch('status', form);
  const isQuarantine = selectedStatus === SegmentStatus.Quarantine;
  const isRejected = selectedStatus === SegmentStatus.Rejected;

  const segmentTypeOptions = useMemo(() => {
    return (
      segmentTypes?.map((value) => ({
        label: value,
        value: value,
      })) || []
    );
  }, [segmentTypes]);

  const statusOptions = useMemo(() => {
    return Object.values(UpdatableSegmentStatuses)
      .filter((s) => isQuarantineEnabled || s !== SegmentStatus.Quarantine)
      .filter((s) => isOnHoldOptionEnabled || s !== SegmentStatus.OnHold)
      .map((status) => ({
        value: status,
        label: t(`chooseSegmentStatus.status.state`, {
          context: status,
        }),
      }));
  }, [t, isQuarantineEnabled, isOnHoldOptionEnabled]);

  const quarantineDefectTypesOptions = useMemo(() => {
    return (
      quarantineDefectTypes.map((type) => ({
        label: type.label,
        value: type.id,
      })) || []
    );
  }, [quarantineDefectTypes]);

  const localizationOptions = useMemo(() => {
    return Object.values(QuarantineDefectOrientationEnum).map(
      (localization) => ({
        value: localization,
        label: t(`chooseSegmentStatus.localization.state`, {
          context: localization,
        }),
      }),
    );
  }, [t]);

  initialValues = {
    ...initialValues,
    ...{
      localization: QuarantineDefectOrientationEnum.Up,
      position: { x: 0, y: 0 },
    },
  };

  return (
    <Form<SegmentFormValues>
      form={form}
      onFinish={save}
      className="flex w-full flex-1 flex-col items-center justify-between px-4 py-4"
      initialValues={initialValues}
    >
      <MainCard>
        <Form.Item<SegmentFormValues>
          name="segmentType"
          label={t('chooseSegmentStatus.form.segmentType')}
          rules={[{ required: true, message: t('common.fieldRequired') }]}
          className="mb-4"
          hidden={!!initialValues.segmentType}
        >
          <Select
            placeholder={t('common.placeholder.select')}
            className="w-full"
            options={segmentTypeOptions}
          />
        </Form.Item>

        <Form.Item<SegmentFormValues>
          name="status"
          label={t('chooseSegmentStatus.form.label')}
          rules={[{ required: true, message: t('common.fieldRequired') }]}
        >
          <Select
            placeholder={t('common.placeholder.select')}
            className="w-full"
            options={statusOptions}
            disabled={initialValues.status != undefined}
          />
        </Form.Item>

        {isQuarantine ? (
          <div className="flex flex-col gap-4">
            <div className="pt-6 text-center text-lg">
              {t('chooseSegmentStatus.form.quarantine.title')}
            </div>
            <Form.Item<SegmentFormValues>
              name="quarantineType"
              label={t('chooseSegmentStatus.form.quarantine.type')}
              rules={[
                { required: isQuarantine, message: t('common.fieldRequired') },
              ]}
            >
              <Select
                placeholder={t('common.placeholder.select')}
                className="w-full"
                options={quarantineDefectTypesOptions}
              />
            </Form.Item>
            <Form.Item<SegmentFormValues>
              name="localization"
              label={t('chooseSegmentStatus.form.quarantine.localization')}
              rules={[
                { required: isQuarantine, message: t('common.fieldRequired') },
              ]}
            >
              <Select
                placeholder={t('common.placeholder.select')}
                className="w-full"
                options={localizationOptions}
              />
            </Form.Item>

            <Form.Item
              name="position"
              noStyle
              getValueProps={(value) => ({ position: value })}
              trigger="setPosition"
            >
              <ImagePointer
                image={
                  selectedDefectLocalization ===
                  QuarantineDefectOrientationEnum.Up
                    ? segmentDrawUp
                    : segmentDrawDown
                }
              />
            </Form.Item>

            <Form.Item<SegmentFormValues>
              name="description"
              label={t('chooseSegmentStatus.form.quarantine.description')}
            >
              <Input.TextArea
                placeholder={t('common.placeholder.inputText')}
                rows={4}
              />
            </Form.Item>

            <Form.Item<SegmentFormValues>
              name="pictures"
              label={t('chooseSegmentStatus.form.quarantine.pictures')}
            >
              <PictureUploadInput maxCount={3} />
            </Form.Item>
          </div>
        ) : null}

        {isRejected ? (
          <div className="flex flex-col gap-4">
            <div className="pt-6 text-center text-lg">
              {t('chooseSegmentStatus.form.rejection.title')}
            </div>
            <Form.Item<SegmentFormValues>
              name="rejectionDescription"
              label={t('chooseSegmentStatus.form.rejection.description')}
            >
              <Input.TextArea
                placeholder={t('common.placeholder.inputText')}
                rows={4}
              />
            </Form.Item>

            <Form.Item<SegmentFormValues>
              name="rejectionPictures"
              label={t('chooseSegmentStatus.form.rejection.pictures')}
            >
              <PictureUploadInput maxCount={3} />
            </Form.Item>
          </div>
        ) : null}
      </MainCard>

      <Button
        type="primary"
        className="mt-4 w-full"
        htmlType="submit"
        loading={isFetching}
      >
        {t('common.validate')}
      </Button>
    </Form>
  );
};
